import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Eye, EyeOff } from "lucide-react";

export default function InputField({
  name,
  label,
  required,
  type = "text",
  inputId,
  placeholder,
  min,
  rules,
  value,
  onChange,
  error,
  ...props
}) {
  const formContext = useFormContext();
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  if (!formContext || !name) {
    return (
      <div className="relative w-full">
        {/* Label */}
        {label && (
          <Label
            htmlFor={inputId}
            className="block mb-1 text-sm font-medium text-secondary capitalize"
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </Label>
        )}

        {/* Regular Input */}
        <Input
          id={inputId}
          min={min}
          type={inputType}
          placeholder={placeholder}
          value={value ?? ""}
          onChange={onChange}
          aria-invalid={!!error}
          className={`rounded-[1rem] h-[3rem] pr-10 ${error ? "border-destructive" : ""}`}
          {...props}
        />

        {/* Password Toggle */}
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((prev) => !prev)}
            className={`absolute right-3 top-[3.2rem] -translate-y-1/2 text-muted-foreground hover:text-foreground`}
          >
            {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
        )}

        {error && (
          <p className="text-red-500 text-xs mt-1">{typeof error === "string" ? error : error.message}</p>
        )}
      </div>
    );
  }

  const { control } = formContext;

  return (
    <div className="relative w-full">
      {/* Label */}
      {label && (
        <Label
          htmlFor={inputId}
          className="block mb-1 text-sm font-medium text-secondary capitalize"
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
      )}

      { }
      {/* Controller */}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <Input
              {...field}
              id={inputId}
              min={min}
              type={inputType}
              placeholder={placeholder}
              aria-invalid={!!error}
              value={field.value ?? ""}
              onChange={(e) => {
                const value =
                  type === "number"
                    ? e.target.value === ""
                      ? ""
                      : Number(e.target.value)
                    : e.target.value;

                field.onChange(value);
              }}
              className={`rounded-[1rem] h-[3rem] pr-10 ${error ? "border-destructive" : ""
                }`}
              {...props}
            />

            {/* Password Toggle */}
            {isPassword && (
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((prev) => !prev)}
                className={`absolute right-3 top-[3.2rem] -translate-y-1/2 text-muted-foreground hover:text-foreground`}
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            )}

            {/* Error Message */}
            {error && (
              <p className="text-red-500 text-xs mt-1">{error?.message}</p>
            )}
          </>
        )}
      />
    </div>
  );
}
